import { useState } from "react";
import { palette } from "./pageStyles";
import { OPERATORS, SOURCES, blankRule, describeRule, sourceDefaults } from "../backtest/rules";

const st = {
  field: { padding: "6px 8px", borderRadius: "4px", border: `1px solid ${palette.border}`, background: "#050608", color: palette.text, fontSize: "0.82rem", outline: "none" },
  btn: { padding: "7px 14px", borderRadius: "4px", border: "none", background: palette.accent, color: palette.accentText, fontWeight: 700, fontSize: "0.82rem", cursor: "pointer" },
  ghost: { padding: "5px 10px", borderRadius: "4px", border: `1px solid ${palette.border}`, background: "transparent", color: palette.text, fontSize: "0.76rem", cursor: "pointer" },
  small: { fontSize: "0.74rem", color: palette.mutedText },
  row: { display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" },
};

const SIDES = [
  { key: "long", title: "Go long when", hint: "Leave empty for a short-only strategy." },
  { key: "short", title: "Go short when", hint: "Optional. Needs \"Allow shorts\" on." },
  { key: "exit", title: "Exit (go flat) when", hint: "Optional. Without it the strategy stays in until the opposite signal." },
];

/** Value for the source <select>: built-in id, "custom:" + JSON [itemId, output], or "number".
 *  (JSON because item ids themselves contain ":".) */
const customKey = (itemId, output) => `custom:${JSON.stringify([itemId, output])}`;

function refKey(ref) {
  if (!ref) return "close";
  if (ref.src === "custom") return customKey(ref.itemId, ref.output || "");
  return ref.src;
}

function refFromKey(key, indicators) {
  if (key === "number") return { src: "number", value: 0 };
  if (key.startsWith("custom:")) {
    let itemId = "";
    let output = "";
    try {
      [itemId, output] = JSON.parse(key.slice(7));
    } catch {
      return { src: "close" };
    }
    return { src: "custom", itemId, output: output || indicators.find((i) => i.id === itemId)?.outputs[0] || "Value" };
  }
  return { src: key, p: sourceDefaults(key) };
}

const isRegimeSource = (source) => /kaufman|efficiency|regime/i.test(`${source.id} ${source.label}`);
const isRegimeStateRef = (ref) => /^(hmm|kaufman)_regime_(range|trend|mixed|high_volatility)$/.test(String(ref?.src || ""))
  || /^d_regime_(range|trend|neutral)$/.test(String(ref?.src || ""));

function SourcePicker({ value, onChange, indicators, allowNumber, label, isAdmin }) {
  const def = SOURCES.find((s) => s.id === value?.src);
  const availableSources = SOURCES.filter((source) => isAdmin || !isRegimeSource(source));
  const customGroup = "My indicators (Python)";
  const [selectedGroup, setSelectedGroup] = useState(null);
  const isHiddenRegimeSource = !isAdmin && def && isRegimeSource(def);
  const inferredGroup = isHiddenRegimeSource ? "All indicators" : value?.src === "number" ? "Number" : value?.src === "custom" ? customGroup : (def?.group || "Price");
  const currentGroup = selectedGroup || inferredGroup;
  const sourceGroups = ["All indicators", ...new Set(availableSources.map((source) => source.group))];
  if (allowNumber) sourceGroups.push("Number");
  if (indicators.length) sourceGroups.push(customGroup);
  const groupSources = currentGroup === customGroup
    ? indicators.flatMap((it) => it.outputs.map((output) => ({ id: customKey(it.id, output), label: it.outputs.length > 1 ? `${it.name} · ${output}` : it.name, custom: true, itemId: it.id, output })))
    : currentGroup === "All indicators" ? availableSources.filter((source) => source.group !== "Price")
    : currentGroup === "Number" ? [{ id: "number", label: "A number" }]
      : currentGroup === "Price" ? availableSources.filter((source) => source.group === "Price")
        : availableSources.filter((source) => source.group === currentGroup);
  const selectedSource = groupSources.some((source) => source.id === refKey(value)) ? refKey(value) : (groupSources[0]?.id || "");
  return (
    <span style={{ ...st.row, gap: 4 }}>
      <select aria-label={`${label} category`} title="Choose indicator category" style={{ ...st.field, maxWidth: 155 }} value={currentGroup} onChange={(e) => {
        const nextGroup = e.target.value;
        setSelectedGroup(nextGroup);
        if (nextGroup === "Number") onChange({ src: "number", value: 0 });
        else if (nextGroup === customGroup) {
          const first = indicators[0];
          if (first) onChange({ src: "custom", itemId: first.id, output: first.outputs[0] || "Value" });
        } else {
          const first = nextGroup === "All indicators"
            ? availableSources.find((source) => source.group !== "Price")
            : availableSources.find((source) => source.group === nextGroup);
          if (first) onChange(refFromKey(first.id, indicators));
        }
      }}>
        {sourceGroups.map((group) => <option key={group} value={group}>{group === "Indicators" ? "Classic" : group}</option>)}
      </select>
      <select aria-label={label} style={{ ...st.field, maxWidth: 260 }} value={selectedSource} onChange={(e) => {
        const selected = groupSources.find((source) => source.id === e.target.value);
        onChange(selected?.custom ? { src: "custom", itemId: selected.itemId, output: selected.output } : refFromKey(e.target.value, indicators));
      }}>
        {groupSources.map((source) => <option key={source.id} value={source.id}>{source.label}</option>)}
      </select>
      {value?.src === "number" && (
        <input aria-label={`${label} value`} style={{ ...st.field, width: 80 }} type="number" step="any" value={value.value} onChange={(e) => onChange({ ...value, value: e.target.value })} />
      )}
      {def?.params?.map((p) => (
        <input
          key={p.key}
          aria-label={`${def.label} ${p.label}`}
          title={p.label}
          style={{ ...st.field, width: 58 }}
          type="number"
          min={p.min ?? 1}
          max={p.max}
          step={p.step || 1}
          value={value.p?.[p.key] ?? p.value}
          onChange={(e) => onChange({ ...value, p: { ...(value.p || {}), [p.key]: e.target.value } })}
        />
      ))}
    </span>
  );
}

/** Create or edit a no-code rule strategy. */
export default function RuleBuilder({ initial, indicators, onSave, onCancel, isAdmin = false }) {
  const [rule, setRule] = useState(() => (initial ? JSON.parse(JSON.stringify(initial)) : blankRule()));
  const [problem, setProblem] = useState("");
  const names = Object.fromEntries(indicators.map((i) => [i.id, { name: i.name, outputs: i.outputs }]));
  const usesHMMRegime = ["long", "short", "exit"].some((side) =>
    (rule[side] || []).some((condition) => [condition.a, condition.b].some((ref) => /^hmm_regime_/.test(String(ref?.src || ""))))
  );
  const usesDRegime = ["long", "short", "exit"].some((side) =>
    (rule[side] || []).some((condition) => [condition.a, condition.b].some((ref) => /^d_regime_/.test(String(ref?.src || ""))))
  );

  const setCond = (side, idx, patch) =>
    setRule((prev) => ({ ...prev, [side]: prev[side].map((c, i) => (i === idx ? { ...c, ...patch } : c)) }));
  const addCond = (side) =>
    setRule((prev) => ({ ...prev, [side]: [...(prev[side] || []), { a: { src: "close" }, op: "xa", b: { src: "sma", p: { period: 20 } } }].slice(0, 4) }));
  const removeCond = (side, idx) => setRule((prev) => ({ ...prev, [side]: prev[side].filter((_, i) => i !== idx) }));

  const save = () => {
    if (!rule.long?.length && !rule.short?.length) {
      setProblem("Add at least one long or short condition.");
      return;
    }
    const name = rule.name.trim() || describeRule(rule, names).slice(0, 40);
    onSave({ ...rule, name });
  };

  return (
    <div style={{ border: `1px solid ${palette.glow}`, borderRadius: 6, padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
      <label style={{ ...st.row, fontSize: "0.8rem" }}>
        Name
        <input style={{ ...st.field, flex: 1, minWidth: 160 }} value={rule.name} maxLength={40} placeholder="e.g. RSI dip buyer" onChange={(e) => setRule({ ...rule, name: e.target.value })} />
      </label>
      {SIDES.map((side) => (
        <div key={side.key} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ fontSize: "0.82rem", fontWeight: 600 }}>{side.title}</div>
          {(rule[side.key] || []).map((cond, idx) => (
            <div key={idx} style={st.row}>
              {idx > 0 && <span style={st.small}>{rule.logic?.[side.key] === "or" ? "OR" : "AND"}</span>}
              <SourcePicker label={`${side.title} condition ${idx + 1} left side`} value={cond.a} indicators={indicators} onChange={(a) => setCond(side.key, idx, { a })} isAdmin={isAdmin} />
              {isRegimeStateRef(cond.a) ? (
                <span style={{ ...st.small, border: `1px solid ${palette.border}`, borderRadius: 4, padding: "7px 9px" }}>is active</span>
              ) : (
                <>
                  <select aria-label={`${side.title} condition ${idx + 1} comparison`} style={st.field} value={cond.op} onChange={(e) => setCond(side.key, idx, { op: e.target.value })}>
                    {OPERATORS.map((o) => (
                      <option key={o.id} value={o.id}>{o.label}</option>
                    ))}
                  </select>
                  <SourcePicker label={`${side.title} condition ${idx + 1} right side`} value={cond.b} indicators={indicators} allowNumber onChange={(b) => setCond(side.key, idx, { b })} isAdmin={isAdmin} />
                </>
              )}
              <button type="button" style={st.ghost} aria-label={`Remove ${side.title} condition ${idx + 1}`} title="Remove this condition" onClick={() => removeCond(side.key, idx)}>✕</button>
            </div>
          ))}
          <div style={st.row}>
            <button type="button" style={st.ghost} onClick={() => addCond(side.key)} disabled={(rule[side.key] || []).length >= 4}>
              + Add {(rule.logic?.[side.key] === "or" ? "OR" : "AND")} condition
            </button>
            {(rule[side.key] || []).length > 0 && (
              <label style={{ ...st.row, ...st.small }}>
                Combine as
                <select
                  aria-label={`${side.title} condition logic`}
                  style={st.field}
                  value={rule.logic?.[side.key] === "or" ? "or" : "and"}
                  onChange={(event) => setRule((prev) => ({ ...prev, logic: { ...(prev.logic || {}), [side.key]: event.target.value } }))}
                >
                  <option value="and">All conditions (AND)</option>
                  <option value="or">Any condition (OR)</option>
                </select>
              </label>
            )}
            <span style={st.small}>{side.hint}</span>
          </div>
        </div>
      ))}
      <div style={{ ...st.small, color: palette.text }}>{describeRule(rule, names)}</div>
      {isAdmin && usesHMMRegime && <div style={st.small}>A selected HMM regime is a direct state condition: for example, HMM Range is true only while the model identifies Range. The model needs at least 400 candles, fits on the first 70%, and exposes forward-filtered states only for the final 30%; earlier candles cannot trigger HMM rules. Its fit score is a data-fit diagnostic, not trading performance.</div>}
      {isAdmin && usesDRegime && <div style={st.small}>D composite state conditions use the previous candle’s state to avoid lookahead. Hurst, variance-ratio, and efficiency cutoffs are calibrated from a seeded 50,000-bar random walk. The attached script warns its candidate settings were not fully validated; check them on your symbol and timeframe.</div>}
      <div style={st.row}>
        <button type="button" style={st.btn} onClick={save}>{initial ? "Save rule" : "Add to list"}</button>
        <button type="button" style={st.ghost} onClick={onCancel}>Cancel</button>
        {problem && <span style={{ color: palette.danger, fontSize: "0.8rem" }}>{problem}</span>}
      </div>
    </div>
  );
}
